import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { formatMoney } from "../../common/utils/format";
import {
  BuyerAddress,
  BuyerWithAddresses,
  ListingSearchResult,
  ManualOrderResult,
  createManualOrder,
  getUserAddresses,
  searchListings,
} from "../../common/requests/orders/endpoints";
import { Banner, Column, DataTable, DetailList, EmptyState, PageShell, Panel, StatusPill, useIsNarrow, useToast } from "../../ui";

const SEARCH_DEBOUNCE_MS = 300;

function formatAddress(address: BuyerAddress) {
  const name = [address.first_name, address.last_name].filter(Boolean).join(" ");
  const line = `${address.address}, ${address.postal_code}, ${address.country}`;
  return name ? `${name} — ${line}` : line;
}

function errorDetail(err: any, fallback: string) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return fallback;
}

interface ManualOrderProps {
  /** Rendered inside the Sales "Create order" dialog: no page frame or title. */
  embedded?: boolean;
  /** Called with the result after an order is created. */
  onCreated?: (result: ManualOrderResult) => void;
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <Panel sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          component="span"
          sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: C.ink, color: C.paper, display: "grid", placeItems: "center", fontSize: 12.5, fontWeight: 700 }}
        >
          {n}
        </Box>
        <Typography variant="h6" component="h3" sx={{ fontSize: 15 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Panel>
  );
}

export default function ManualOrder({ embedded = false, onCreated }: ManualOrderProps) {
  const { idToken } = useAuth();
  const toast = useToast();
  const narrow = useIsNarrow(600);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ListingSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [listing, setListing] = useState<ListingSearchResult | null>(null);

  const [buyerId, setBuyerId] = useState("");
  const [buyer, setBuyer] = useState<BuyerWithAddresses | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [buyerError, setBuyerError] = useState<string | null>(null);
  const [addressId, setAddressId] = useState("");
  const [quantity, setQuantity] = useState(1);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // A recent order for the same buyer + listing exists; creating again needs an explicit second click.
  const [possibleDuplicate, setPossibleDuplicate] = useState(false);
  // The order was created but something after it failed: retrying would make a second order.
  const [createdButIncomplete, setCreatedButIncomplete] = useState(false);

  useEffect(() => {
    const term = query.trim();
    if (!idToken || !term) {
      setResults([]);
      setSearchError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchListings(term, idToken);
        if (!cancelled) {
          setResults(res.data);
          setSearchError(null);
        }
      } catch (err: any) {
        if (!cancelled) setSearchError(errorDetail(err, "Failed to search listings."));
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, idToken]);

  const lookUpBuyer = async () => {
    const id = buyerId.trim();
    if (!idToken || !id) return;
    setLookingUp(true);
    setBuyer(null);
    setAddressId("");
    setBuyerError(null);
    try {
      const res = await getUserAddresses(id, idToken);
      setBuyer(res.data);
      const primary = res.data.addresses.find((a) => a.is_primary) || res.data.addresses[0];
      setAddressId(primary ? primary.address_id : "");
      if (res.data.addresses.length === 0) {
        setBuyerError("This user has no saved addresses. They must add one before an order can be created.");
      }
    } catch (err: any) {
      setBuyerError(errorDetail(err, "Failed to look up user."));
    } finally {
      setLookingUp(false);
    }
  };

  const resetForm = () => {
    setListing(null);
    setBuyerId("");
    setBuyer(null);
    setAddressId("");
    setQuantity(1);
    setQuery("");
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setSubmitError(null);
    setPossibleDuplicate(false);
    if (createdButIncomplete) {
      setCreatedButIncomplete(false);
      resetForm();
    }
  };

  const submitOrder = async (allowDuplicate = false) => {
    if (!idToken || !listing || !buyer || !addressId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await createManualOrder(
        {
          listing_id: listing.listing_id,
          buyer_id: buyer.user.user_id,
          address_id: addressId,
          quantity,
          allow_duplicate: allowDuplicate,
        },
        idToken
      );
      setConfirmOpen(false);
      setPossibleDuplicate(false);
      toast(
        `Order ${res.data.order_id} created: ${formatMoney(res.data.total, res.data.currency)}` +
          (res.data.delivery_total ? ` incl. ${formatMoney(res.data.delivery_total, res.data.currency)} delivery` : ""),
        "success"
      );
      resetForm();
      onCreated?.(res.data);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setSubmitError(errorDetail(err, "Failed to create the order."));
      setPossibleDuplicate(err?.response?.status === 409 && detail?.code === "possible_duplicate");
      // Payments says the order exists but a later step failed, or we never got an answer.
      const message = typeof detail === "string" ? detail : "";
      setCreatedButIncomplete(/was created/i.test(message) || !err?.response);
    } finally {
      setSubmitting(false);
    }
  };

  const isSingleItem = listing?.is_one !== false;
  const selectedAddress = buyer?.addresses.find((a) => a.address_id === addressId);
  const canSubmit = Boolean(listing && buyer && addressId && quantity >= 1);

  const listingColumns: Column<ListingSearchResult>[] = [
    {
      key: "listing",
      header: "Listing",
      card: "title",
      render: (row) => (
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", minWidth: 0 }}>
          {row.gcs_uri && (
            <Box component="img" src={row.gcs_uri} alt={row.name} sx={{ width: 36, height: 36, objectFit: "cover", borderRadius: "6px", display: "block", flexShrink: 0 }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{row.name}</Box>
            <Box className="mono" sx={{ fontSize: 12, color: C.muted, fontWeight: 400, overflowWrap: "anywhere" }}>
              {row.listing_id}
            </Box>
          </Box>
        </Box>
      ),
    },
    { key: "seller", header: "Seller", card: "subtitle", render: (row) => row.seller_username || row.seller_id },
    {
      key: "price",
      header: "Price",
      card: "aside",
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
          {row.currency} {row.price}
        </Box>
      ),
    },
    {
      key: "status",
      header: "Status",
      card: "aside",
      render: (row) => <StatusPill tone={row.status === "Available" ? "success" : "outline"}>{row.status || "-"}</StatusPill>,
    },
  ];

  const body = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" sx={{ color: C.muted }}>
        Buy a listing on behalf of a user without taking payment. The order goes through the normal post-payment flow.
      </Typography>

      <Step n={1} title="Listing">
        <TextField
          size="small"
          label="Search listings"
          placeholder="Listing name or listing ID"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        {searchError && <Banner tone="danger">{searchError}</Banner>}
        {query.trim() && !searchError && (searching || results.length > 0) ? (
          <Box sx={{ maxHeight: 360, overflowY: "auto", minWidth: 0 }}>
            <DataTable
              aria-label="Listings"
              dense
              cardBelow={600}
              rows={results}
              loading={searching}
              columns={listingColumns}
              getRowId={(row) => row.listing_id}
              selectedId={listing?.listing_id ?? null}
              onRowClick={(row) => {
                if (row.status !== "Available") return;
                setListing(row);
                setQuantity(1);
              }}
            />
          </Box>
        ) : query.trim() && !searchError ? (
          <EmptyState title="No listings found." />
        ) : null}
        {listing && (
          <Banner tone="success" title="Selected:">
            {listing.name} ({listing.currency} {listing.price}) from {listing.seller_username || listing.seller_id}
          </Banner>
        )}
      </Step>

      <Step n={2} title="Buyer">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) auto" }, gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            label="Buyer"
            placeholder="User ID, username or email"
            value={buyerId}
            onChange={(event) => setBuyerId(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") lookUpBuyer();
            }}
          />
          <Button variant="outlined" onClick={lookUpBuyer} disabled={!buyerId.trim() || lookingUp} sx={{ minHeight: 44 }}>
            {lookingUp ? <CircularProgress size={20} /> : "Look up"}
          </Button>
        </Box>
        {buyerError && <Banner tone={buyer ? "warning" : "danger"}>{buyerError}</Banner>}
        {buyer && (
          <>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              <strong>{buyer.user.username || "(no username)"}</strong> · {buyer.user.email}
            </Typography>
            {buyer.addresses.length > 0 && (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) 120px" }, gap: 1, alignItems: "start" }}>
                <TextField select size="small" label="Shipping address" value={addressId} onChange={(event) => setAddressId(event.target.value)}>
                  {buyer.addresses.map((address) => (
                    <MenuItem key={address.address_id} value={address.address_id} sx={{ whiteSpace: "normal" }}>
                      {formatAddress(address)}
                      {address.is_primary ? " (primary)" : ""}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label="Quantity"
                  value={quantity}
                  disabled={isSingleItem}
                  helperText={isSingleItem ? "Single item" : undefined}
                  inputProps={{ min: 1 }}
                  onChange={(event) => setQuantity(Math.max(1, Number(event.target.value) || 1))}
                />
              </Box>
            )}
          </>
        )}
      </Step>

      <Box>
        <Button variant="contained" disabled={!canSubmit} onClick={() => setConfirmOpen(true)} sx={{ minHeight: 44, width: { xs: "100%", sm: "auto" } }}>
          Create order
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={() => !submitting && closeConfirm()} fullWidth maxWidth="sm" fullScreen={narrow}>
        <DialogTitle>Confirm manual order</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {listing && buyer && (
            <DetailList
              items={[
                { label: "Listing", value: listing.name, full: true },
                { label: "Price", value: `${listing.currency} ${listing.price} × ${quantity} (+ delivery)` },
                { label: "Seller", value: listing.seller_username || listing.seller_id },
                { label: "Buyer", value: `${buyer.user.username || buyer.user.user_id} · ${buyer.user.email}`, full: true },
                ...(selectedAddress ? [{ label: "Ship to", value: formatAddress(selectedAddress), full: true }] : []),
              ]}
            />
          )}
          <Banner tone="warning" title="No payment will be taken.">
            The seller will be credited normally, the listing will be marked sold, and the buyer and seller will be notified.
          </Banner>
          {submitError && (
            <Alert severity={possibleDuplicate ? "warning" : "error"}>
              {submitError}
              {createdButIncomplete && <> Don't create it again: check the order in Sales first.</>}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ pb: narrow ? "calc(12px + env(safe-area-inset-bottom, 0px))" : undefined, "& > *": { flex: { xs: 1, sm: "0 0 auto" } } }}>
          <Button variant="outlined" onClick={closeConfirm} disabled={submitting}>
            {createdButIncomplete ? "Close" : "Cancel"}
          </Button>
          {possibleDuplicate ? (
            <Button variant="contained" onClick={() => submitOrder(true)} disabled={submitting}>
              {submitting ? <CircularProgress size={20} color="inherit" /> : "Create another order anyway"}
            </Button>
          ) : (
            <Button variant="contained" onClick={() => submitOrder()} disabled={submitting || createdButIncomplete}>
              {submitting ? <CircularProgress size={20} color="inherit" /> : "Create order"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) return body;
  return <PageShell title="Create order">{body}</PageShell>;
}
